import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import type { UseFormTrigger } from 'react-hook-form';
import { createPatientInKinnserKinnserPatientsPost } from '../../services/api/sdk.gen';
import type { KinnserCreatePatientResponse } from '../../services/api/types.gen';
import type { CreatePatientFormData } from './types';
import {
  getSurfaceableError,
  type SurfaceableResultFields,
} from '../../utils/apiErrorDetail';

/** Create result plus the optional surfaceable-business-error fields. */
export type KinnserCreateResult = KinnserCreatePatientResponse &
  SurfaceableResultFields & { message?: string };

interface UseKinnserCreationOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  trigger: UseFormTrigger<CreatePatientFormData>;
  /** When true, skips react-hook-form validation before calling the API. Useful for specialized flows like ROC scheduling. */
  skipValidation?: boolean;
}

interface UseKinnserCreationReturn {
  creatingInKinnser: boolean;
  createResult: KinnserCreateResult | null;
  handleCreateInKinnser: () => Promise<void>;
}

/**
 * Hook for creating patients in Kinnser.
 * Manages the creation state and handles the API call.
 */
export function useKinnserCreation({
  intakeId,
  intakeStatus,
  trigger,
  skipValidation = false,
}: UseKinnserCreationOptions): UseKinnserCreationReturn {
  const [creatingInKinnser, setCreatingInKinnser] = useState(false);
  const [createResult, setCreateResult] = useState<KinnserCreateResult | null>(
    null
  );

  const clearKinnserCreateResult = useCallback(() => {
    setCreateResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearKinnserCreateResult
  );

  const handleCreateInKinnser = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API unless explicitly skipped
    if (!skipValidation) {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }
    }

    setCreatingInKinnser(true);
    setCreateResult(null);

    try {
      const response = await createPatientInKinnserKinnserPatientsPost({
        body: { intake_id: intakeId },
      });

      if (response.data) {
        setCreateResult(response.data);
      } else {
        // hey-api does not throw here; a 409 surfaceable error (or any non-2xx)
        // arrives on response.error and was previously dropped silently.
        const surfaceable = getSurfaceableError(response);
        setCreateResult({
          success: false,
          kinnser_patient_id: null,
          errors: [
            {
              field: 'general',
              code: surfaceable?.errorCode ?? 'API_ERROR',
              message:
                surfaceable?.message ??
                'Failed to create patient. Please try again.',
            },
          ],
          ...(surfaceable
            ? {
                surfaceable: true,
                error_code: surfaceable.errorCode,
                message: surfaceable.message,
              }
            : {}),
        });
      }
    } catch (error) {
      console.error('Error creating patient in Kinnser:', error);
      setCreateResult({
        success: false,
        kinnser_patient_id: null,
        errors: [
          {
            field: 'general',
            code: 'UNEXPECTED_ERROR',
            message: 'An unexpected error occurred. Please try again.',
          },
        ],
      });
    } finally {
      setCreatingInKinnser(false);
    }
  }, [intakeId, trigger, skipValidation]);

  return {
    creatingInKinnser,
    createResult,
    handleCreateInKinnser,
  };
}
